import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography
} from '@mui/material';

const ALL_ROLES = ['someac_superuser', 'someac_owner', 'someac_guest', 'sabal_app_user'];

export default function EditRolesDialog({ open, user, onClose, onSave }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    if (!open) return;
    setIsSubmitting(false);
    setFormError('');
    setRoles(Array.isArray(user?.roles) ? user.roles : []);
  }, [open, user]);

  const canSubmit = useMemo(() => {
    if (!user?.userId) return false;
    if (!roles.length) return false;
    return true;
  }, [roles.length, user?.userId]);

  const handleClose = () => {
    if (isSubmitting) return;
    setFormError('');
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!user?.userId) return;
    setIsSubmitting(true);
    setFormError('');

    try {
      const result = await onSave?.(user.userId, roles);
      if (result?.ok === false) {
        setFormError(result.message || 'Failed to update roles');
        setIsSubmitting(false);
        return;
      }
      setIsSubmitting(false);
      onClose?.();
    } catch (e) {
      setIsSubmitting(false);
      setFormError(e?.message || 'Failed to update roles');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth aria-labelledby="edit-roles-dialog-title">
      <DialogTitle id="edit-roles-dialog-title">Edit Roles</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError && <Alert severity="error">{formError}</Alert>}

          <Box>
            <Typography variant="caption" color="text.secondary">User</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {user?.sabalLoginEmail || `User #${user?.userId || '—'}`}
            </Typography>
          </Box>

          <FormControl>
            <InputLabel id="edit-roles-label">Roles</InputLabel>
            <Select
              labelId="edit-roles-label"
              multiple
              value={roles}
              onChange={(e) => setRoles(e.target.value)}
              input={<OutlinedInput label="Roles" />}
              disabled={isSubmitting}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((v) => <Chip key={v} size="small" label={v} />)}
                </Box>
              )}
            >
              {ALL_ROLES.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Replace semantics. DBAM enforces last-superuser protection.
            </Typography>
          </FormControl>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isSubmitting || !canSubmit} variant="contained">
          {isSubmitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


